import { AMPLITUDE_EVENTS, trackAnalytics } from './baglessAnalytics';

const SKIP_IDS = new Set([
  'webgl',
  'css3d',
  'virtualRealityContainer',
  'chat-list-navbar-button',
  'people-list-navbar-button',
]);

const SKIP_CLOSEST = [
  '#webgl',
  '#css3d',
  'canvas',
  '.indicator',
  '.vrai-agent-chair',
  '#chat-list-navbar-button',
  '#people-list-navbar-button',
  '.chat-card',
  '.icon-send',
  '.chat-container .back-button',
  '.chat-container .icon-close',
  '.chat-panel .icon-close',
  '.header-actions__close-btn',
];

const BUTTON_EVENTS = {
  'guideButton': 'User clicked tour',
  'infoButton': 'User clicked space info',
  'locationsButton': 'User clicked locations',
  'tuneSettings': 'User clicked tune menu',
  'burger-menu': 'User clicked menu',
  'exit_app': 'User clicked exit',
  'toggleModes': 'User clicked edit room',
  'editRoom': 'User clicked edit room',
  'showSpaceEditRoom': 'User clicked edit room',
  'fullscreen_toggle': 'User clicked fullscreen',
  'page_media_mute_toggle': 'User clicked page mute',
  'create-postit-button': 'User clicked create postit',
  'shareToggle': 'User clicked share',
  'help_center': 'User clicked help center',
  'usetiful_help': 'User clicked help',
  'open-setting-menu': 'User clicked settings',
  'oneClickTakeSeat': 'User clicked take a seat',
  'toggleLock': 'User clicked lock room',
  'user-menu-toggle': 'User clicked profile menu',
  'closeTuneMenu': 'User closed tune menu',
  'showSpaceInfo': 'User clicked space info',
  'lang-selector-toggle': 'User clicked language',
  'toggleAudioTrack': 'User clicked microphone',
  'toggleVideoTrack': 'User clicked camera',
  'toggleVapiAudioTrack': 'User clicked VAPI microphone',
  'toggleVapiVideoTrack': 'User clicked VAPI camera',
  'leave-chair': 'User clicked leave seat',
  'leave-vapi-call': 'User clicked leave VAPI call',
  'toggleElementsVisibility': 'User clicked toggle elements',
  'copyWorldName': 'User clicked copy space name',
  'backToWorldsMenu': 'User clicked back to spaces',
  'trigger-notification-settings': 'User clicked notification settings',
  'trigger-device-settings': 'User clicked device settings',
  'welcome-screen-option': 'User clicked welcome screen',
  'location_name': 'User clicked location name',
  'room_name': 'User clicked space name',
  'chess-toggleAudioTrack': 'User clicked microphone',
  'chess-toggleVideoTrack': 'User clicked camera',
  'chess-leave-chair': 'User clicked leave seat',
  'chess-flip': 'User clicked chess flip',
  'chess-restart': 'User clicked chess restart',
  'chess-lock': 'User clicked chess lock',
  'chess-history': 'User clicked chess history',
  'chatPvt-inviteToScene': 'User clicked invite to scene',
  'chatPvt-jumpToScene': 'User clicked jump to scene',
  'chatPvt-removeFromFriends': 'User clicked remove from friends',
  'chatPvt-showInfo': 'User clicked view profile',
  'app-login': 'User clicked login',
  'app-logout': 'User clicked logout',
  'mediaSettings': 'User clicked media settings',
  'editProfile': 'User clicked edit profile',
  'viewInfo': 'User clicked view info',
  'preview_mode': 'User clicked preview',
  'publish_changes': 'User clicked publish',
  'lockEditor': 'User clicked lock editor',
};

const CLASS_EVENTS = [
  { match: 'own_chair__controls__screen-share', event: 'User clicked screen share' },
  { match: 'own_chair__controls__stop-screen-share', event: 'User clicked stop screen share' },
  { match: 'own_chair__controls__toggle-meeting-mode', event: 'User clicked meeting mode' },
  { match: 'own_chair__controls__mic', event: 'User clicked microphone' },
  { match: 'own_chair__controls__camera', event: 'User clicked camera' },
  { match: 'icon-close', event: 'User clicked close' },
  { match: 'modal-close', event: 'User clicked close modal' },
  { match: 'close-modal', event: 'User clicked close modal' },
  { match: 'leave_preview_mode', event: 'User clicked leave preview' },
  { match: 'icon-emoji', event: 'User clicked emoji' },
];

let clickN = 0;

function labelFromEl(el) {
  return (
    el.getAttribute('aria-label')
    || el.getAttribute('title')
    || el.getAttribute('data-lang')
    || el.id
    || (el.className && String(el.className).split(' ').filter(Boolean)[0])
    || (el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 40)
    || 'unknown'
  );
}

function findTrackable(el) {
  if (!el || el.nodeType !== 1) return null;
  return el.closest([
    'button',
    '[role="button"]',
    'a[href]',
    '.clickable',
    '.buttn',
    '.world-bottom-nav__area__item',
    '.world-top-nav__menu_area',
    '.menu-item',
    '.bagless-dropdown-menu li',
    '.icon-close',
    '.icon-emoji',
    '.modal-close',
    '.close-modal',
    '.switch-button',
    '.nav-menu__group-item',
    '.message-input__emoji-menu__item',
    '[data-role]',
  ].join(','));
}

function shouldSkip(el) {
  if (!el) return true;
  if (SKIP_IDS.has(el.id)) return true;
  if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  return SKIP_CLOSEST.some((sel) => el.closest(sel));
}

function eventForEl(el) {
  if (el.id && BUTTON_EVENTS[el.id]) return { event: BUTTON_EVENTS[el.id], button_id: el.id };
  const withId = el.closest('[id]');
  if (withId?.id && BUTTON_EVENTS[withId.id]) {
    return { event: BUTTON_EVENTS[withId.id], button_id: withId.id };
  }
  const className = String(el.className || '');
  for (const item of CLASS_EVENTS) {
    if (className.includes(item.match) || el.closest(`.${item.match}`)) {
      return { event: item.event, button_id: el.id || item.match };
    }
  }
  const id = el.id || withId?.id || '';
  const label = labelFromEl(el);
  return {
    event: AMPLITUDE_EVENTS.CLICKED_UI,
    button_id: id || label,
  };
}

export function installUiButtonTracking(model) {
  if (typeof document === 'undefined' || document.__baglessUiTrackInstalled) return;
  document.__baglessUiTrackInstalled = true;

  document.addEventListener('click', (e) => {
    const raw = e.target;
    if (!(raw instanceof Element)) return;
    const el = findTrackable(raw);
    if (!el || shouldSkip(el)) return;
    const mapped = eventForEl(el);
    if (!mapped?.event) return;
    clickN += 1;
    trackAnalytics(model, mapped.event, {
      button_id: mapped.button_id,
      button_label: labelFromEl(el),
      click_n: clickN,
    });
  }, true);
}
